import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators'; // Importez switchMap
import { User } from '../user/users.models';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { UserFirm } from '../user-firm/user-firm.models'; // Importez UserFirm
import { Firm } from '../firm/firm.models'; // Importez Firm
import { Profile } from '../profile/profile.models';
import {
  RegisterApiResponse,
  LoginQueryParams,
  LoginApiResponse,
  SendEmailResetPasswordApiResponse,
  ResetPasswordApiResponse,
} from './auth.models';
import { ApiError } from '../error-handling/error.models'; // Importez ApiError ici
import { ErrorHandlingService } from '../error-handling/error-handling.service';
import { hasProperty } from '../../helpers/utils';
import { IauthService } from './auth.models';

@Injectable({ providedIn: 'root' })
export class AuthenticationService<Type> implements IauthService<Type> {
  private apiUrl = environment.apiUrl + '/api/';
  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>; // permet de s'abonner aux changements de l'état de l'utilisateur. C'est utile si besoin de réagir en temps réel aux modifications de l'état de l'utilisateur

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private errorHandlingService: ErrorHandlingService
  ) {
    this.currentUserSubject = new BehaviorSubject<User>(
      JSON.parse(this.localStorageService.getItem('currentUser')!)
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  // renvoie directement la valeur actuelle de l'utilisateur connecté (sans avoir besoin de s'abonner à un observable)
  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  public set userValue(user: User) {
    this.localStorageService.setItem('currentUser', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  create(apiName: string, data: any): Observable<Type | ApiError> {
    return this.http
      .post<Type | ApiError>(`${this.apiUrl}${apiName}`, data)
      .pipe(catchError(this.handleError));
  }

  getElement(apiName: string): Observable<Type | ApiError> {
    return this.http
      .get<Type | ApiError>(`${this.apiUrl}${apiName}`)
      .pipe(catchError(this.handleError));
  }

  login(
    queryParams: LoginQueryParams
  ): Observable<LoginApiResponse | ApiError> {
    return this.http
      .post<LoginApiResponse | ApiError>(`${this.apiUrl}login`, queryParams)
      .pipe(
        map((response) => {
          if (this.isLoginApiResponse(response)) {
            this.localStorageService.setItem('authToken', response.token);
            this.localStorageService.setItem(
              'refreshToken',
              response.refresh_token
            );
          }
          return response;
        }),
        catchError(this.handleError)
      );
  }

  loginBySwitch(uri: string): Observable<LoginApiResponse | ApiError> {
    return this.http
      .post<LoginApiResponse | ApiError>(`${this.apiUrl}${uri}`, {})
      .pipe(
        map((response) => {
          if (this.isLoginApiResponse(response)) {
            this.currentUserSubject.next(null!);
            this.localStorageService.setItem('authToken', response.token);
            this.localStorageService.setItem(
              'refreshToken',
              response.refresh_token
            );
          }
          return response;
        }),
        catchError(this.handleError)
      );
  }

  getFirmContext() {
    return this.http.get(`${this.apiUrl}firm-context`).pipe(
      map((response) => {
        this.localStorageService.setItem(
          'firmContext',
          JSON.stringify(response)
        );
        return response;
      }),
      catchError(this.handleError)
    );
  }

  currentUserInfo(): Observable<User | ApiError> {
    const authToken = this.localStorageService.getItem('authToken');
    if (!authToken) {
      const missingTokenError: ApiError = {
        isError: true,
        message: 'JWT Token not found',
        statusCode: 401,
        details: null,
      };
      return of(missingTokenError);

    }

    return this.http.get<User | ApiError>(`${this.apiUrl}users/me`).pipe(
      switchMap((userResponse) => {
        if (this.isUser(userResponse) && userResponse.activeUserFirm) {
          return this.http.get<UserFirm | ApiError>(`${environment.apiUrl}${userResponse.activeUserFirm}`).pipe(
            switchMap((userFirmResponse) => {
              if (this.isUserFirm(userFirmResponse)) {
                const firmRequest: Observable<Firm | ApiError | null> = userFirmResponse.firm
                  ? this.http.get<Firm | ApiError>(`${environment.apiUrl}${typeof userFirmResponse.firm === 'string' ? userFirmResponse.firm : userFirmResponse.firm['@id']}`)
                  : of(null);
                const profileRequest: Observable<Profile | ApiError | null> = typeof userFirmResponse.profile === 'string'
                  ? this.http.get<Profile | ApiError>(`${environment.apiUrl}${userFirmResponse.profile}`)
                  : of(userFirmResponse.profile as Profile | null);

                return firmRequest.pipe(
                  switchMap((firmResponse) =>
                    profileRequest.pipe(
                      map((profileResponse) => {
                        if (firmResponse !== null && this.isFirm(firmResponse)) {
                          // Enrichir l'objet user avec le nom du cabinet actif
                          (userResponse as any).activeFirmName = firmResponse.name;
                        }

                        if (this.isProfile(profileResponse)) {
                          userResponse.activeProfileCode = profileResponse.code;
                        }

                        return userResponse;
                      })
                    )
                  )
                );
              }
              return of(userResponse);
            }),
            catchError((error: HttpErrorResponse) =>
              error.status === 404 ? of(userResponse) : throwError(() => error)
            )
          );
        }
        return of(userResponse);
      }),
      map((user) => {
        if (this.isUser(user)) {
          this.localStorageService.setItem('currentUser', JSON.stringify(user));
          this.currentUserSubject.next(user);
        }
        return user;
      }),
      catchError(this.handleError)
    );
  }

  logout(): void {
    this.localStorageService.clear();
    this.currentUserSubject.next(null!);
  }

  refreshToken(): Observable<Type | ApiError> {
    const refresh_token = this.localStorageService.getItem('refreshToken');
    return this.create('token/refresh', { refresh_token });
  }

  isUser(object: User | ApiError): object is User {
    return hasProperty(
      object,
      'id',
      (value): value is string => typeof value === 'string'
    );
  }

  isLoginApiResponse(
    response: LoginApiResponse | ApiError
  ): response is LoginApiResponse {
    return hasProperty(
      response,
      'token',
      (value): value is string => typeof value === 'string'
    );
  }

  isRegisterApiResponse(
    response: RegisterApiResponse | ApiError
  ): response is RegisterApiResponse {
    return hasProperty(
      response,
      'id',
      (value): value is string => typeof value === 'string'
    );
  }

  isSendEmailResetPasswordApiReponse(
    response: SendEmailResetPasswordApiResponse | ApiError
  ): response is SendEmailResetPasswordApiResponse {
    return hasProperty(
      response,
      'id',
      (value): value is string => typeof value === 'string'
    );
  }

  isResetPasswordApiResponse(
    response: ResetPasswordApiResponse | ApiError
  ): response is ResetPasswordApiResponse {
    return hasProperty(
      response,
      'id',
      (value): value is string => typeof value === 'string'
    );
  }

  isUserFirm(object: UserFirm | ApiError): object is UserFirm {
    return hasProperty(
      object,
      'id',
      (value): value is string => typeof value === 'string'
    );
  }

  isFirm(object: Firm | ApiError): object is Firm {
    return hasProperty(
      object,
      'id',
      (value): value is string => typeof value === 'string'
    );
  }

  isProfile(object: Profile | ApiError | null): object is Profile {
    if (object === null) {
      return false;
    }

    return hasProperty(
      object,
      'code',
      (value): value is string => typeof value === 'string'
    );
  }

  private matchesAccessToken(token: string): boolean {
    if (!this.currentUserValue) {
      return false;
    }

    return this.currentUserValue.roles.includes(token)
      || this.currentUserValue.activeProfileCode === token;
  }

  // Méthode pour vérifier si l'utilisateur a un rôle spécifique
  hasRole(role: string): boolean {
    return this.matchesAccessToken(role);
  }

  // Méthode pour vérifier si l'utilisateur a l'un des rôles requis
  hasAnyRole(roles: string[]): boolean {
    return roles.some((role) => this.matchesAccessToken(role));
  }

  // Méthode pour vérifier si l'utilisateur a tous les rôles requis
  hasAllRoles(roles: string[]): boolean {
    return roles.every((role) => this.matchesAccessToken(role));
  }

  hasCurrentProfileCode(profileCode: string): boolean {
    return this.currentUserValue?.activeProfileCode === profileCode;
  }

  isCurrentUserExpert(): boolean {
    return this.hasCurrentProfileCode('PROFILE_CPA');
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    // Gestion de l'erreur
    const apiError = this.errorHandlingService.handleError(error);
    // Utilise throwError pour signaler une erreur
    // L'erreur ira directement au bloc error si celui-ci est défini dans le subscribe
    return throwError(() => apiError);
  };
}
